import { useEffect, useState } from "react";
import axios from "axios";
import { Wallet, TrendingUp, TrendingDown, Shield, RefreshCw } from "lucide-react";

/**
 * PublicTransparencyDashboard — Section publique de transparence financière.
 * Utilise /api/finances/public-summary (no PIN, no personal data).
 */
const CAT_LABEL = {
  gala: "🎉 Gala", academie: "⚽ Académie", odon_itsou: "🥁 Odôn-Itsou",
  sante_avc: "🏥 Santé AVC", admin: "📋 Administration",
  siege: "🏠 Siège", autre: "◾ Autre",
};
const ENTITY_LABEL = { uk: "🇬🇧 SAFUAYE UK", togo: "🇹🇬 SAFUAYE Togo" };
const fmtFCFA = (n) => (Math.round(n) || 0).toLocaleString("fr-FR") + " FCFA";

export default function PublicTransparencyDashboard({ API, t }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/finances/public-summary`);
      setData(r.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <section id="transparency" data-testid="transparency-section"
             className="py-20 md:py-24 bg-gradient-to-b from-white via-emerald-50/40 to-white relative overflow-hidden">
      <div className="max-w-6xl mx-auto px-4 md:px-8 relative">
        <div className="text-center mb-10">
          <span className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-emerald-700 font-semibold mb-3">
            <Shield className="w-4 h-4" /> Transparence financière
          </span>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-5xl font-bold text-gray-900 mb-3" data-testid="transparency-title">
            {t?.transparency?.title || "Nos comptes, en toute clarté"}
          </h2>
          <p className="text-base md:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {t?.transparency?.intro || "SAFUAYE publie l'état consolidé de sa trésorerie (SAFUAYE UK + SAFUAYE Togo). Les libellés individuels et les pièces justificatives sont audités par la Charity Commission UK — cette page n'affiche que des agrégats anonymisés."}
          </p>
        </div>

        {loading || !data ? (
          <div className="text-center py-16 text-gray-500 flex items-center justify-center gap-2">
            <RefreshCw className="w-4 h-4 animate-spin" /> Chargement des données…
          </div>
        ) : (
          <>
            {/* Cartes récap */}
            <div className="grid md:grid-cols-4 gap-3 mb-6">
              <Card icon={<TrendingUp className="w-5 h-5" />} title="Entrées de fonds"
                    value={fmtFCFA(data.total_in)} color="bg-emerald-600" testId="pub-total-in" />
              <Card icon={<TrendingDown className="w-5 h-5" />} title="Dépenses"
                    value={fmtFCFA(data.total_out)} color="bg-red-600" testId="pub-total-out" />
              <Card icon={<Wallet className="w-5 h-5" />} title="Solde net"
                    value={fmtFCFA(data.balance)}
                    color={data.balance >= 0 ? "bg-navy-800" : "bg-orange-600"}
                    testId="pub-balance" />
              <Card icon={<Shield className="w-5 h-5" />} title="Couverture justificatifs"
                    value={`${data.receipt_coverage_pct}%`}
                    color="bg-blue-700" testId="pub-coverage"
                    subtitle={`${data.expenses_with_receipt}/${data.expenses_total} dépenses justifiées`} />
            </div>

            {/* Two columns: Categories + Entities */}
            <div className="grid md:grid-cols-2 gap-4">
              {/* Par catégorie */}
              <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200" data-testid="pub-by-cat">
                <p className="text-xs font-bold uppercase text-slate-700 mb-3">Répartition par catégorie</p>
                <div className="space-y-2">
                  {data.by_category.map(c => {
                    const pctIn = data.total_in > 0 ? (c.in / data.total_in * 100) : 0;
                    const pctOut = data.total_out > 0 ? (c.out / data.total_out * 100) : 0;
                    return (
                      <div key={c.category} className="text-sm" data-testid={`pub-cat-${c.category}`}>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="font-semibold">{CAT_LABEL[c.category] || c.category}</span>
                          <span className={`font-mono ${c.balance < 0 ? "text-red-700" : "text-emerald-700"}`}>
                            {fmtFCFA(c.balance)}
                          </span>
                        </div>
                        <div className="flex h-3 rounded overflow-hidden bg-slate-100">
                          {c.in > 0 && <div className="bg-emerald-500" style={{ width: `${pctIn}%` }} />}
                          {c.out > 0 && <div className="bg-red-500" style={{ width: `${pctOut}%` }} />}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Par entité (UK / Togo) */}
              <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200" data-testid="pub-by-entity">
                <p className="text-xs font-bold uppercase text-slate-700 mb-3">Répartition par entité</p>
                <div className="space-y-2">
                  {data.by_entity.filter(e => e.in > 0 || e.out > 0).map(e => (
                    <div key={e.entity} className="border rounded p-3" data-testid={`pub-entity-${e.entity}`}>
                      <div className="flex justify-between items-center mb-1">
                        <p className="font-bold text-gray-900">{ENTITY_LABEL[e.entity] || e.entity}</p>
                        <span className={`text-sm font-mono font-bold ${e.balance < 0 ? "text-red-700" : "text-emerald-700"}`}>
                          {fmtFCFA(e.balance)}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-[11px]">
                        <div className="bg-emerald-50 rounded p-1.5 text-emerald-800">
                          Entrées <br/><span className="font-mono font-bold">{fmtFCFA(e.in)}</span>
                        </div>
                        <div className="bg-red-50 rounded p-1.5 text-red-800">
                          Dépenses <br/><span className="font-mono font-bold">{fmtFCFA(e.out)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                  {data.by_entity.every(e => e.in === 0 && e.out === 0) && (
                    <p className="text-xs text-gray-500 py-4">Pas encore de données par entité.</p>
                  )}
                </div>
                <p className="mt-3 text-[10px] text-gray-500 italic">
                  SAFUAYE Togo est parrainée par SAFUAYE UK — les fonds sont consolidés en FCFA.
                </p>
              </div>
            </div>

            {/* Timeline par mois */}
            {data.by_month.length > 0 && (
              <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 mt-4" data-testid="pub-by-month">
                <p className="text-xs font-bold uppercase text-slate-700 mb-3">Évolution mensuelle</p>
                <div className="flex items-end gap-2 h-32 overflow-x-auto">
                  {data.by_month.map(m => {
                    const max = Math.max(...data.by_month.map(x => Math.max(x.in, x.out))) || 1;
                    return (
                      <div key={m.month} className="flex flex-col items-center gap-1 min-w-[40px]"
                           data-testid={`pub-month-${m.month}`}>
                        <div className="flex items-end gap-0.5 h-24">
                          <div className="bg-emerald-500 rounded-t" title={`Entrées: ${fmtFCFA(m.in)}`}
                               style={{ height: `${(m.in / max) * 96}px`, width: "14px" }} />
                          <div className="bg-red-500 rounded-t" title={`Dépenses: ${fmtFCFA(m.out)}`}
                               style={{ height: `${(m.out / max) * 96}px`, width: "14px" }} />
                        </div>
                        <span className="text-[9px] font-mono text-gray-600">{m.month}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="flex gap-4 mt-2 text-[10px] text-gray-500">
                  <span><span className="inline-block w-2 h-2 bg-emerald-500 mr-1" />Entrées</span>
                  <span><span className="inline-block w-2 h-2 bg-red-500 mr-1" />Dépenses</span>
                </div>
              </div>
            )}

            {/* Disclaimer + last update */}
            <div className="mt-6 bg-slate-50 border-l-4 border-emerald-600 p-3 text-xs text-gray-700">
              <p><strong>{data.transaction_count}</strong> transactions consolidées ·
                {" "}Dernière mise à jour : {new Date(data.last_updated).toLocaleString("fr-FR")}</p>
              <p className="mt-1 italic">{data.disclaimer}</p>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function Card({ icon, title, value, color, testId, subtitle }) {
  return (
    <div className={`${color} text-white p-4 rounded-lg shadow`} data-testid={testId}>
      <div className="flex items-center gap-2 opacity-90 text-xs uppercase font-semibold">
        {icon} {title}
      </div>
      <p className="mt-1 text-2xl font-bold font-mono">{value}</p>
      {subtitle && <p className="text-[10px] opacity-80 mt-0.5">{subtitle}</p>}
    </div>
  );
}
